import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Alert, Button, DatePicker, Input, Select, Table } from "antd";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import {
  CardTitle,
  PageHeader,
  SectionCard,
  StatStrip,
  StatusTag,
  fmtDate,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconExcel, IconSearch } from "../components/ui/icons";
import notify from "../utils/notify";

const { RangePicker } = DatePicker;

const PRESETS = [
  { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  { label: "Next 7 days", value: [dayjs().startOf("day"), dayjs().add(7, "day").endOf("day")] },
  { label: "This week", value: [dayjs().startOf("week"), dayjs().endOf("week")] },
  { label: "This month", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
  { label: "Last month", value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
  { label: "Last 30 days", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Last 90 days", value: [dayjs().subtract(89, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "This year", value: [dayjs().startOf("year"), dayjs().endOf("year")] },
];

const nightsBetween = (a, b) => {
  if (!a || !b) return "";
  const n = Math.round((new Date(b) - new Date(a)) / 86400000);
  return Number.isFinite(n) && n >= 0 ? n : "";
};

const ReservationReportsPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [statistics, setStatistics] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState({ stats: false, timeline: false, rooms: false });
  const [error, setError] = useState(null);

  // filters sent to the API (check-in date range, room)
  const [roomId, setRoomId] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  // filters applied in the browser on the loaded bookings
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const fetchRooms = async () => {
    if (!hotelId) return;
    setLoading((prev) => ({ ...prev, rooms: true }));
    try {
      const response = await axiosInstance.get(`/Rooms/all/${hotelId}`);
      setRooms(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      console.error("Error fetching rooms:", e);
    } finally {
      setLoading((prev) => ({ ...prev, rooms: false }));
    }
  };

  // The API compares the check-in time with the end date, so the end must be the END of that day
  const apiParams = useCallback(
    () => ({
      hotelId,
      ...(roomId !== "all" && { roomId }),
      ...(dateRange?.[0] && { startDate: dayjs(dateRange[0]).startOf("day").toISOString() }),
      ...(dateRange?.[1] && { endDate: dayjs(dateRange[1]).endOf("day").toISOString() }),
    }),
    [hotelId, roomId, dateRange]
  );

  // `override` lets "Clear" reload with no filters straight away (state updates are not applied yet at that point)
  const load = useCallback(async (override) => {
    if (!hotelId) return;
    setError(null);
    setLoading((prev) => ({ ...prev, stats: true, timeline: true }));
    const params = override && override.hotelId ? override : apiParams();
    try {
      const [s, t] = await Promise.all([
        axiosInstance.get("/RoomStatistics", { params }),
        axiosInstance.get("/RoomStatistics/timeline", { params }),
      ]);
      setStatistics(s.data);
      setTimeline(t.data);
    } catch (e) {
      console.error("Error loading the report:", e);
      setError(e?.response?.data?.message || "Could not load the report.");
    } finally {
      setLoading((prev) => ({ ...prev, stats: false, timeline: false }));
    }
  }, [hotelId, apiParams]);

  useEffect(() => {
    if (hotelId) {
      fetchRooms();
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const clearFilters = () => {
    setRoomId("all");
    setDateRange([]);
    setStatus("all");
    setSearch("");
    load({ hotelId });
  };

  const bookings = useMemo(() => statistics?.bookings || [], [statistics]);

  const statusOptions = useMemo(() => {
    const set = Array.from(new Set(bookings.map((b) => b.bookingStatus).filter(Boolean)));
    return [{ value: "all", label: "All statuses" }, ...set.map((s) => ({ value: s, label: s }))];
  }, [bookings]);

  const visibleBookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (status !== "all" && b.bookingStatus !== status) return false;
      if (!q) return true;
      return `${b.guestName || ""} ${b.roomNumber || ""} ${b.roomType || ""}`.toLowerCase().includes(q);
    });
  }, [bookings, status, search]);

  const timelineRows = useMemo(
    () =>
      (timeline || []).flatMap((day) =>
        (day.bookings || []).map((b, i) => ({ ...b, key: `${day.date}-${b.roomNumber}-${i}`, day: day.date }))
      ),
    [timeline]
  );

  /* ───────── export ───────── */
  const exportExcel = () => {
    if (!visibleBookings.length) {
      notify.info("There are no bookings to export.");
      return;
    }

    const rows = visibleBookings.map((b) => ({
      Room: b.roomNumber,
      "Room type": b.roomType || "Unknown",
      Guest: b.guestName,
      "Check-in": b.checkInDate ? fmtDate(b.checkInDate) : "",
      "Check-out": b.checkOutDate ? fmtDate(b.checkOutDate) : "",
      Nights: nightsBetween(b.checkInDate, b.checkOutDate),
      Status: b.bookingStatus,
      "Booked on": b.bookingMadeOn ? fmtDate(b.bookingMadeOn) : "",
    }));

    const roomLabel =
      roomId === "all" ? "All rooms" : rooms.find((r) => r.roomId === roomId)?.roomNumber ? `Room ${rooms.find((r) => r.roomId === roomId).roomNumber}` : `Room ${roomId}`;

    const summary = [
      ["Reservation report"],
      ["Generated", dayjs().format("DD MMM YYYY HH:mm")],
      ["Check-in from", dateRange?.[0] ? dayjs(dateRange[0]).format("DD MMM YYYY") : "Any"],
      ["Check-in to", dateRange?.[1] ? dayjs(dateRange[1]).format("DD MMM YYYY") : "Any"],
      ["Room", roomLabel],
      ["Status", status === "all" ? "All" : status],
      ["Search", search || "-"],
      [],
      ["Bookings in this export", visibleBookings.length],
      ["Total bookings (before status and search)", statistics?.totalBookings ?? bookings.length],
      ["Checked in", statistics?.occupiedBookings ?? ""],
      ["Occupancy rate (%)", statistics?.occupancyRate != null ? Number(statistics.occupancyRate).toFixed(2) : ""],
      ["Average stay (days)", statistics?.averageBookingDurationDays != null ? Number(statistics.averageBookingDurationDays).toFixed(2) : ""],
    ];

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 10 }, { wch: 18 }, { wch: 26 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 14 }];
    const wsSummary = XLSX.utils.aoa_to_sheet(summary);
    wsSummary["!cols"] = [{ wch: 40 }, { wch: 24 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `reservation-report_${dayjs().format("YYYY-MM-DD")}.xlsx`
    );
    notify.success(`Exported ${rows.length} booking${rows.length === 1 ? "" : "s"}`);
  };

  /* ───────── tables ───────── */
  const columns = [
    {
      title: "Room",
      dataIndex: "roomNumber",
      key: "roomNumber",
      sorter: (a, b) => String(a.roomNumber).localeCompare(String(b.roomNumber)),
      render: (v, r) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{v}</span>
          <span className="hms-cell-sub">{r.roomType || "Unknown type"}</span>
        </div>
      ),
    },
    { title: "Guest", dataIndex: "guestName", key: "guestName", sorter: (a, b) => String(a.guestName).localeCompare(String(b.guestName)) },
    {
      title: "Check-in",
      dataIndex: "checkInDate",
      key: "checkInDate",
      sorter: (a, b) => new Date(a.checkInDate) - new Date(b.checkInDate),
      render: (d) => (d ? fmtDate(d) : "—"),
    },
    {
      title: "Check-out",
      dataIndex: "checkOutDate",
      key: "checkOutDate",
      sorter: (a, b) => new Date(a.checkOutDate) - new Date(b.checkOutDate),
      render: (d) => (d ? fmtDate(d) : "—"),
    },
    { title: "Nights", key: "nights", align: "right", width: 90, render: (_, b) => nightsBetween(b.checkInDate, b.checkOutDate) },
    { title: "Status", dataIndex: "bookingStatus", key: "bookingStatus", width: 150, render: (s) => <StatusTag status={s}>{s}</StatusTag> },
    { title: "Booked on", dataIndex: "bookingMadeOn", key: "bookingMadeOn", render: (d) => (d ? fmtDate(d) : "—") },
  ];

  const timelineColumns = [
    { title: "Date", dataIndex: "day", key: "day", width: 140, render: (d) => <span className="hms-cell-main">{fmtDate(d)}</span> },
    { title: "Room", dataIndex: "roomNumber", key: "roomNumber", width: 100 },
    { title: "Guest", dataIndex: "guestName", key: "guestName" },
    {
      title: "Stay",
      key: "stay",
      render: (_, b) => `${fmtDate(b.checkInDate)}${b.checkOutDate ? ` to ${fmtDate(b.checkOutDate)}` : ""}`,
    },
    { title: "Status", dataIndex: "bookingStatus", key: "bookingStatus", width: 150, render: (s) => <StatusTag status={s}>{s}</StatusTag> },
  ];

  const filtersActive = roomId !== "all" || dateRange?.length || status !== "all" || search;

  return (
    <div className="hms-page rr-page">
      <PageHeader
        actions={
          <Button type="primary" icon={<IconExcel />} onClick={exportExcel} disabled={!visibleBookings.length}>
            Export to Excel
          </Button>
        }
      />

      {error && <Alert style={{ marginBottom: 12 }} type="error" showIcon title="Could not load the report" description={error} />}

      <div className="hms-stack">
        <SectionCard title="Filters">
          <div className="hms-filters">
            <div className="hms-field">
              <label>Check-in date</label>
              <RangePicker
                presets={PRESETS}
                value={dateRange && dateRange.length ? dateRange : null}
                onChange={(v) => setDateRange(v || [])}
                allowClear
                style={{ width: 280 }}
                format="DD MMM YYYY"
              />
            </div>
            <div className="hms-field">
              <label>Room</label>
              <Select
                style={{ width: 220 }}
                value={roomId}
                onChange={setRoomId}
                loading={loading.rooms}
                showSearch
                optionFilterProp="label"
                options={[
                  { value: "all", label: "All rooms" },
                  ...rooms.map((r) => ({ value: r.roomId, label: `${r.roomNumber} - ${r.roomTypeName || "Unknown type"}` })),
                ]}
              />
            </div>
            <div className="hms-field">
              <label>Status</label>
              <Select style={{ width: 170 }} value={status} onChange={setStatus} options={statusOptions} />
            </div>
            <div className="hms-field">
              <label>Search</label>
              <Input
                allowClear
                style={{ width: 220 }}
                prefix={<IconSearch />}
                placeholder="Guest, room or type"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="hms-filter-actions">
              <Button onClick={clearFilters} disabled={!filtersActive}>
                Clear
              </Button>
              <Button type="primary" loading={loading.stats || loading.timeline} onClick={() => load()}>
                Apply filters
              </Button>
            </div>
          </div>
          <p className="hms-card-hint" style={{ padding: "10px 0 0" }}>
            The date range and room are applied when you click Apply filters. Status and search filter the bookings below straight away.
          </p>
        </SectionCard>

        <SectionCard flush title="Summary">
          {statistics ? (
            <StatStrip
              items={[
                { label: "Total bookings", value: statistics.totalBookings },
                { label: "Checked in", value: statistics.occupiedBookings },
                { label: "Occupancy rate", value: Number(statistics.occupancyRate ?? 0).toFixed(2), suffix: "%" },
                { label: "Average stay", value: Number(statistics.averageBookingDurationDays ?? 0).toFixed(2), suffix: "days" },
              ]}
            />
          ) : (
            <Alert style={{ margin: 16 }} type="info" showIcon title="No statistics yet" description="Apply filters to see booking statistics." />
          )}
        </SectionCard>

        <SectionCard flush title={<CardTitle count={visibleBookings.length}>Bookings</CardTitle>}>
          <Table
            className="hms-table"
            columns={columns}
            dataSource={visibleBookings}
            loading={loading.stats}
            rowKey={(r) => `${r.roomNumber}-${r.checkInDate}-${r.guestName}-${r.bookingMadeOn}`}
            pagination={tablePagination("booking")}
            scroll={{ x: 900 }}
            locale={{ emptyText: <div className="hms-empty">No bookings for these filters</div> }}
          />
        </SectionCard>

        <SectionCard flush title={<CardTitle count={timelineRows.length}>Occupancy timeline</CardTitle>}>
          <Table
            className="hms-table"
            columns={timelineColumns}
            dataSource={timelineRows}
            loading={loading.timeline}
            rowKey="key"
            pagination={tablePagination("entry")}
            scroll={{ x: 700 }}
            locale={{ emptyText: <div className="hms-empty">No occupancy data for these filters</div> }}
          />
        </SectionCard>
      </div>
    </div>
  );
};

export default ReservationReportsPage;
